import type { ArchiveImport, RefusedEntry } from '../../api/library'
import { Button } from '../../ui/Control'
import { asSize } from './Tiles'

/**
 * What is happening to the files that were just handed over.
 *
 * The requirement behind this is plain: while files are going up, somebody has to be able to see what
 * is happening and what has finished. So each file says which of the four things it is — waiting, on
 * its way with how far it has got, arrived, or refused with the reason — and an archive additionally
 * says what came out of it.
 *
 * It is a list on the page and not a dialog. Nothing here stands in the way of looking at the folder
 * while the rest of the files arrive, and nothing has to be dismissed before work can go on.
 */
export type UploadState =
  | 'waiting'
  | 'sending'
  | 'arrived'
  /** The content was already in the library, so it joined the document that is made of it. */
  | 'joined'
  | 'refused'

export interface Upload {
  readonly id: number
  readonly name: string
  readonly state: UploadState
  /** How much has gone out, from 0 to 1, or `null` when the browser will not say. */
  readonly sent: number | null
  /** Why it was refused, in words. */
  readonly reason?: string
  /** For an archive: what came in and what did not. */
  readonly report?: ArchiveImport
}

export interface UploadsProps {
  readonly uploads: readonly Upload[]
  /** Clears the ones that are finished with. Never offered while something is still going up. */
  readonly onClear: () => void
}

export function Uploads({ uploads, onClear }: UploadsProps) {
  if (uploads.length === 0) {
    return null
  }

  const busy = uploads.some((upload) => upload.state === 'waiting' || upload.state === 'sending')

  return (
    <section className="uploads" aria-label="Files being added">
      <ul className="uploads__list">
        {uploads.map((upload) => (
          <li className="uploads__item" key={upload.id} data-state={upload.state}>
            <p className="uploads__name">{upload.name}</p>
            <p className="uploads__state">{said(upload)}</p>
            {upload.state === 'sending' && (
              <div
                className="uploads__bar"
                role="progressbar"
                aria-label={`Uploading ${upload.name}`}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={upload.sent === null ? undefined : Math.round(upload.sent * 100)}
              >
                <span
                  className="uploads__filled"
                  style={{ width: `${Math.round((upload.sent ?? 0.5) * 100)}%` }}
                />
              </div>
            )}
            {upload.report !== undefined && <Report report={upload.report} />}
          </li>
        ))}
      </ul>
      {!busy && (
        <div className="controls controls--tight">
          <Button tone="bare" onClick={onClear}>
            Clear this list
          </Button>
        </div>
      )}
    </section>
  )
}

function said(upload: Upload): string {
  switch (upload.state) {
    case 'waiting':
      return 'Waiting'
    case 'sending':
      return upload.sent === null ? 'On its way' : `${Math.round(upload.sent * 100)}%`
    case 'arrived':
      return 'Added'
    case 'joined':
      return 'Already there'
    case 'refused':
      return upload.reason ?? 'Not added'
  }
}

/**
 * What an archive brought, and what it did not.
 *
 * The second half is the point. An import of two hundred files that quietly leaves out the four
 * oversized ones is four files nobody notices are missing until they are needed.
 */
function Report({ report }: { readonly report: ArchiveImport }) {
  return (
    <div className="uploads__report">
      <p>
        {counted(report.imported.filter((one) => !one.merged).length, 'document')} came in
        {report.imported.some((one) => one.merged) &&
          `, ${report.imported.filter((one) => one.merged).length} were already there`}
        {report.refused.length > 0 && `, ${report.refused.length} did not`}
      </p>
      {report.refused.length > 0 && (
        <ul className="uploads__refused">
          {report.refused.map((entry) => (
            <li key={entry.name}>
              {entry.name} — {why(entry)}
            </li>
          ))}
        </ul>
      )}
      {report.stoppedEarly && (
        <p className="uploads__note">
          That archive holds more files than one import takes. What is listed came in; the rest was
          not read.
        </p>
      )}
    </div>
  )
}

/** "1 document", "4 documents" — the plural said once rather than in four places. */
function counted(count: number, thing: string): string {
  return count === 1 ? `1 ${thing}` : `${count} ${thing}s`
}

function why(entry: RefusedEntry): string {
  switch (entry.reason) {
    case 'too-large':
      return entry.sizeInBytes === null
        ? 'larger than this installation takes'
        : `${asSize(entry.sizeInBytes)}, larger than this installation takes`
    case 'not-a-path':
      return 'its name is not one MyLib can put anywhere'
    case 'too-many':
      return 'the archive holds too many files'
    default:
      return 'not added'
  }
}
